import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Download } from "lucide-react";

const STATUS_LABEL = { pending_briefing: "Pending Briefing", blocked: "Blocked", checked_in: "Checked In", checked_out: "Checked Out" };
const ALL_VALUE = "__all__";

export default function VisitorLogs() {
  const [visits, setVisits] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [gates, setGates] = useState([]);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", host_id: "", status: "", purpose: "", list_hit: "", gate_id: "" });

  const activeParams = () => Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

  const load = async () => {
    const { data } = await api.get("/visits", { params: activeParams() });
    setVisits(data);
  };

  useEffect(() => {
    load();
    api.get("/users/hosts").then(({ data }) => setHosts(data));
    api.get("/purposes").then(({ data }) => setPurposes(data));
    api.get("/gates").then(({ data }) => setGates(data));
  }, []);

  const exportCsv = async () => {
    const res = await api.get("/visits/export/csv", { params: activeParams(), responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "visitor_logs.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 space-y-6" data-testid="visitor-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan">Records</span>
          <h1 className="text-3xl font-bold uppercase tracking-tight mt-1 text-brand-navy">Visitor Logs</h1>
        </div>
        <Button
          onClick={exportCsv}
          data-testid="export-csv-button"
          className="rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm gap-2"
        >
          <Download size={16} /> Export CSV
        </Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <Input
          data-testid="filter-date-from"
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          className="w-40 rounded-sm bg-white border-slate-300"
        />
        <Input
          data-testid="filter-date-to"
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          className="w-40 rounded-sm bg-white border-slate-300"
        />
        <Select value={filters.host_id || ALL_VALUE} onValueChange={(v) => setFilters({ ...filters, host_id: v === ALL_VALUE ? "" : v })}>
          <SelectTrigger data-testid="filter-host-select" className="w-40 rounded-sm bg-white border-slate-300">
            <SelectValue placeholder="Host" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All Hosts</SelectItem>
            {hosts.map((h) => (
              <SelectItem key={h.id} value={h.id}>
                {h.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.gate_id || ALL_VALUE} onValueChange={(v) => setFilters({ ...filters, gate_id: v === ALL_VALUE ? "" : v })}>
          <SelectTrigger data-testid="filter-plant-select" className="w-40 rounded-sm bg-white border-slate-300">
            <SelectValue placeholder="Plant" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All Plants</SelectItem>
            {gates.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                {g.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.purpose || ALL_VALUE} onValueChange={(v) => setFilters({ ...filters, purpose: v === ALL_VALUE ? "" : v })}>
          <SelectTrigger data-testid="filter-purpose-select" className="w-40 rounded-sm bg-white border-slate-300">
            <SelectValue placeholder="Purpose" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All Purposes</SelectItem>
            {purposes.map((p) => (
              <SelectItem key={p.id} value={p.name}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.status || ALL_VALUE} onValueChange={(v) => setFilters({ ...filters, status: v === ALL_VALUE ? "" : v })}>
          <SelectTrigger data-testid="filter-status-select" className="w-40 rounded-sm bg-white border-slate-300">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All Statuses</SelectItem>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.list_hit || ALL_VALUE} onValueChange={(v) => setFilters({ ...filters, list_hit: v === ALL_VALUE ? "" : v })}>
          <SelectTrigger data-testid="filter-list-hit-select" className="w-40 rounded-sm bg-white border-slate-300">
            <SelectValue placeholder="List Hit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>Any List Hit</SelectItem>
            <SelectItem value="watchlist">Watchlist</SelectItem>
            <SelectItem value="blacklist">Blacklist</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load} data-testid="apply-filters-button" variant="outline" className="rounded-sm border-slate-300 text-slate-700">
          Apply Filters
        </Button>
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto hidden sm:block">
        <table className="w-full text-sm" data-testid="visitor-logs-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-slate-200">
              <th className="p-3">Visitor ID</th>
              <th className="p-3">Visitor</th>
              <th className="p-3">Mobile</th>
              <th className="p-3">Email</th>
              <th className="p-3">ID Number</th>
              <th className="p-3">ID Type</th>
              <th className="p-3">Plant</th>
              <th className="p-3">Host</th>
              <th className="p-3">Purpose</th>
              <th className="p-3">Status</th>
              <th className="p-3">List Hit</th>
              <th className="p-3">Badge</th>
              <th className="p-3">Check In</th>
              <th className="p-3">Check Out</th>
            </tr>
          </thead>
          <tbody>
            {visits.map((v) => (
              <tr key={v.id} data-testid={`visit-row-${v.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="p-3 text-slate-400 font-mono">{v.visitor_id || "-"}</td>
                <td className="p-3 text-brand-navy font-medium">{v.visitor_name}</td>
                <td className="p-3 text-slate-500">{v.visitor_phone || "-"}</td>
                <td className="p-3 text-slate-500">{v.visitor_email || "-"}</td>
                <td className="p-3 text-slate-500">{v.id_number || "-"}</td>
                <td className="p-3 text-slate-500">{v.id_type || "-"}</td>
                <td className="p-3 text-slate-500">{v.gate_name || "-"}</td>
                <td className="p-3 text-slate-500">{v.host_name}</td>
                <td className="p-3 text-slate-500">{v.purpose}</td>
                <td className="p-3 text-slate-700">{STATUS_LABEL[v.status] || v.status}</td>
                <td className="p-3">
                  {v.list_match?.matched ? (
                    <span className={`text-xs uppercase font-bold ${v.list_match.list_type === "blacklist" ? "text-red-600" : "text-amber-600"}`}>
                      {v.list_match.list_type}
                    </span>
                  ) : (
                    <span className="text-slate-300">-</span>
                  )}
                </td>
                <td className="p-3 text-slate-500 font-mono">{v.badge_number || "-"}</td>
                <td className="p-3 text-slate-500">{v.check_in_time ? new Date(v.check_in_time).toLocaleString() : "-"}</td>
                <td className="p-3 text-slate-500">{v.check_out_time ? new Date(v.check_out_time).toLocaleString() : "-"}</td>
              </tr>
            ))}
            {!visits.length && (
              <tr>
                <td colSpan={14} className="p-6 text-center text-slate-400">
                  No visits recorded yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="sm:hidden space-y-3" data-testid="visitor-logs-mobile-list">
        {visits.map((v) => (
          <div key={v.id} data-testid={`visit-card-${v.id}`} className="border border-slate-200 bg-white p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-bold text-brand-navy">
                {v.visitor_name} <span className="text-xs text-slate-400 font-mono">#{v.visitor_id}</span>
              </p>
              {v.list_match?.matched ? (
                <span className={`text-xs uppercase font-bold ${v.list_match.list_type === "blacklist" ? "text-red-600" : "text-amber-600"}`}>
                  {v.list_match.list_type}
                </span>
              ) : (
                <span className="text-xs uppercase font-bold text-slate-700">{STATUS_LABEL[v.status] || v.status}</span>
              )}
            </div>
            <div className="text-sm text-slate-500 space-y-1">
              <p>
                <span className="text-slate-400">Mobile:</span> {v.visitor_phone || "-"}
              </p>
              <p>
                <span className="text-slate-400">Email:</span> {v.visitor_email || "-"}
              </p>
              <p>
                <span className="text-slate-400">ID:</span> {v.id_number || "-"} {v.id_type ? `(${v.id_type})` : ""}
              </p>
              <p>
                <span className="text-slate-400">Plant:</span> {v.gate_name || "-"}
              </p>
              <p>
                <span className="text-slate-400">Host:</span> {v.host_name}
              </p>
              <p>
                <span className="text-slate-400">Purpose:</span> {v.purpose}
              </p>
              <p>
                <span className="text-slate-400">Badge:</span> {v.badge_number || "-"}
              </p>
              <p>
                <span className="text-slate-400">Check In:</span> {v.check_in_time ? new Date(v.check_in_time).toLocaleString() : "-"}
              </p>
              <p>
                <span className="text-slate-400">Check Out:</span> {v.check_out_time ? new Date(v.check_out_time).toLocaleString() : "-"}
              </p>
            </div>
          </div>
        ))}
        {!visits.length && <p className="p-6 text-center text-slate-400 border border-slate-200 bg-white">No visits recorded yet</p>}
      </div>
    </div>
  );
}
